import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { formatCurrency } from '../lib/formatCurrency';
import { ClipboardCheck, Plus, CheckCircle2, XCircle, MessageSquareWarning, Clock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_META = {
  pending: { label: 'Pending', cls: 'bg-amber-100 dark:bg-amber-500/10 text-amber-700 dark:text-amber-400' },
  approved: { label: 'Approved', cls: 'bg-emerald-100 dark:bg-emerald-500/10 text-emerald-700 dark:text-emerald-400' },
  rejected: { label: 'Rejected', cls: 'bg-rose-100 dark:bg-rose-500/10 text-rose-700 dark:text-rose-400' },
  changes_requested: { label: 'Changes Requested', cls: 'bg-blue-100 dark:bg-blue-500/10 text-blue-700 dark:text-blue-400' },
};

const TYPES = ['contract', 'budget', 'payment', 'recruitment', 'leave', 'travel', 'project', 'claim', 'procurement', 'policy', 'tender', 'capex', 'other'];

export default function ApprovalsPage() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [comment, setComment] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [createForm, setCreateForm] = useState({ type: 'other', title: '', description: '', amount: '', priority: 'normal' });
  const [acting, setActing] = useState(false);
  const canApprove = ['super_admin', 'admin', 'manager'].includes(user?.role);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/approvals`);
      setItems(res.data);
    } catch (e) {
      toast.error('Failed to load approvals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = tab === 'all' ? items : items.filter(i => i.status === tab);

  const submitCreate = async () => {
    if (!createForm.title.trim()) return toast.error('Title is required');
    try {
      await axios.post(`${API}/approvals`, {
        ...createForm,
        amount: createForm.amount === '' ? null : parseFloat(createForm.amount),
      });
      toast.success('Approval request submitted');
      setShowCreate(false);
      setCreateForm({ type: 'other', title: '', description: '', amount: '', priority: 'normal' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    }
  };

  const act = async (action) => {
    setActing(true);
    try {
      const res = await axios.post(`${API}/approvals/${selected.id}/action`, { action, comment });
      toast.success(`Request ${res.data.status.replace('_', ' ')}`);
      setSelected(null);
      setComment('');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally {
      setActing(false);
    }
  };

  const actionable = selected && canApprove && selected.requester_id !== user?.id &&
    ['pending', 'changes_requested'].includes(selected.status);

  return (
    <div className="space-y-6" data-testid="approvals-page">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h1 className="text-3xl font-bold tracking-tight flex items-center gap-2" data-testid="approvals-title">
            <ClipboardCheck className="h-7 w-7 text-primary" /> Executive Approvals
          </h1>
          <p className="text-muted-foreground mt-1">Central approval inbox — every decision traceable</p>
        </div>
        <Button className="rounded-xl gap-2" onClick={() => setShowCreate(true)} data-testid="new-approval-btn">
          <Plus className="h-4 w-4" /> New Request
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="approvals-tabs">
          <TabsTrigger value="pending" data-testid="tab-pending">Pending ({items.filter(i => i.status === 'pending').length})</TabsTrigger>
          <TabsTrigger value="changes_requested" data-testid="tab-changes">Changes</TabsTrigger>
          <TabsTrigger value="approved" data-testid="tab-approved">Approved</TabsTrigger>
          <TabsTrigger value="rejected" data-testid="tab-rejected">Rejected</TabsTrigger>
          <TabsTrigger value="all" data-testid="tab-all">All</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="!p-0">
        <CardContent className="p-0 divide-y">
          {loading ? (
            <div className="p-8 text-center text-muted-foreground">Loading…</div>
          ) : filtered.length ? filtered.map(a => (
            <div key={a.id} className="flex items-center gap-3 p-4 hover:bg-muted/40 cursor-pointer"
              onClick={() => { setSelected(a); setComment(''); }} data-testid={`approval-row-${a.id}`}>
              <Badge variant="outline" className="capitalize shrink-0">{a.type}</Badge>
              <div className="min-w-0 flex-1">
                <p className="font-medium truncate">{a.title}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {a.requester_name} · {new Date(a.created_at).toLocaleDateString('en-ZA')}
                  {a.amount != null ? ` · ${formatCurrency(a.amount)}` : ''}
                </p>
              </div>
              {a.priority === 'high' && <Badge className="bg-rose-100 dark:bg-rose-500/10 text-rose-600 shrink-0" variant="secondary">High</Badge>}
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full shrink-0 ${STATUS_META[a.status]?.cls}`}>
                {STATUS_META[a.status]?.label || a.status}
              </span>
            </div>
          )) : (
            <div className="p-10 text-center text-muted-foreground" data-testid="approvals-empty">
              <ClipboardCheck className="h-10 w-10 mx-auto mb-2 opacity-40" />
              No {tab === 'all' ? '' : tab.replace('_', ' ')} requests
            </div>
          )}
        </CardContent>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-lg" data-testid="approval-detail-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Badge variant="outline" className="capitalize">{selected.type}</Badge> {selected.title}
                </DialogTitle>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                <div className="flex items-center gap-2">
                  <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_META[selected.status]?.cls}`}>{STATUS_META[selected.status]?.label}</span>
                  {selected.amount != null && <span className="font-mono font-semibold">{formatCurrency(selected.amount)}</span>}
                </div>
                <p className="text-muted-foreground">{selected.description || 'No description'}</p>
                <p className="text-xs text-muted-foreground">Requested by <b>{selected.requester_name}</b> on {new Date(selected.created_at).toLocaleString('en-ZA')}</p>
                <div className="border rounded-xl p-3 max-h-40 overflow-y-auto space-y-2" data-testid="approval-history">
                  <p className="text-xs font-semibold text-muted-foreground uppercase">History</p>
                  {(selected.history || []).map((h, i) => (
                    <div key={i} className="flex items-start gap-2 text-xs">
                      <Clock className="h-3.5 w-3.5 mt-0.5 text-muted-foreground shrink-0" />
                      <div>
                        <span className="font-medium capitalize">{h.action.replace('_', ' ')}</span> by {h.by_name}
                        {h.comment && <span className="text-muted-foreground"> — "{h.comment}"</span>}
                        <span className="text-muted-foreground block">{new Date(h.timestamp).toLocaleString('en-ZA')}</span>
                      </div>
                    </div>
                  ))}
                </div>
                {actionable && (
                  <Textarea placeholder="Comment (optional, recommended for reject/changes)"
                    value={comment} onChange={e => setComment(e.target.value)} data-testid="approval-comment-input" />
                )}
              </div>
              {actionable && (
                <DialogFooter className="gap-2 flex-wrap">
                  <Button variant="outline" className="gap-1 text-blue-600" disabled={acting} onClick={() => act('request_changes')} data-testid="request-changes-btn">
                    <MessageSquareWarning className="h-4 w-4" /> Request Changes
                  </Button>
                  <Button variant="outline" className="gap-1 text-rose-600" disabled={acting} onClick={() => act('reject')} data-testid="reject-btn">
                    <XCircle className="h-4 w-4" /> Reject
                  </Button>
                  <Button className="gap-1 bg-emerald-600 hover:bg-emerald-700" disabled={acting} onClick={() => act('approve')} data-testid="approve-btn">
                    <CheckCircle2 className="h-4 w-4" /> Approve
                  </Button>
                </DialogFooter>
              )}
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Create dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent data-testid="approval-create-dialog">
          <DialogHeader><DialogTitle>New Approval Request</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Type</Label>
                <Select value={createForm.type} onValueChange={v => setCreateForm({ ...createForm, type: v })}>
                  <SelectTrigger data-testid="approval-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TYPES.map(t => <SelectItem key={t} value={t} className="capitalize">{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Priority</Label>
                <Select value={createForm.priority} onValueChange={v => setCreateForm({ ...createForm, priority: v })}>
                  <SelectTrigger data-testid="approval-priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="normal">Normal</SelectItem>
                    <SelectItem value="high">High</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Title *</Label>
              <Input value={createForm.title} onChange={e => setCreateForm({ ...createForm, title: e.target.value })} data-testid="approval-title-input" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea value={createForm.description} onChange={e => setCreateForm({ ...createForm, description: e.target.value })} data-testid="approval-desc-input" />
            </div>
            <div>
              <Label>Amount (ZAR, optional)</Label>
              <Input type="number" value={createForm.amount} onChange={e => setCreateForm({ ...createForm, amount: e.target.value })} data-testid="approval-amount-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button onClick={submitCreate} data-testid="approval-submit-btn">Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
